import { useSelectedConversation } from "@/contexts/SelectedConversationContext";
import { Colors } from "@/enums/Colors";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { LazoChatConversation } from "@/models/ai-chat/LazoChatConversation";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import { LAZO_APPLICATIONS_OPTIONS_SERVICE } from "@/types/LazoApplicationOptions";
import { MoreHoriz } from "@mui/icons-material";
import { Box, Button, MenuItem, SxProps, ToggleButton, Typography, styled } from "@mui/material";
import React, { useState } from "react";
import EditChatIcon from "../../../../assets/images/PencilSimple.svg";
import DeleteIcon from "../../../../assets/images/TrashRed.svg";
import UserSVG from "../../../../assets/images/User.svg";
import ArchiveIcon from "../../../../assets/images/archive-gray-logo.svg";
import UnarchiveIcon from "../../../../assets/images/unarchive.svg";
import { UnreadMessagesCountComponent } from "../UnreadMessagesCountComponent";
import { ArchiveConversationModal } from "../chat-options/ArchiveConversationModal";
import { DeleteConversationModal } from "../chat-options/DeleteConversationModal";
import { RenameConversationModal } from "../chat-options/RenameConversationModal";
import { UndoDeleteConversationModal } from "../chat-options/UndoDeleteConversationModal";
import UserListModal from "./UserListModal";

import { CommonStyledMenu } from "@/components/common/CommonStyledMenu";

type ConversationDrawerButtonProps = {
  conversation: LazoChatConversation;
  optionalFunction?: () => void;
};

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: ".5rem",
  "&.red-item": {
    color: Colors.DIM_RED,
    ".MuiSvgIcon-root": { color: Colors.DIM_RED },
  },
}));

const ConversationDrawerButtonSx: SxProps = {
  "&.Mui-selected, &.MuiToggleButton-root:hover": {
    backgroundColor: Colors.ALMOST_WHITE,
  },
  justifyContent: "flex-start",
  width: "90%",
  color: Colors.ELEGANT_BLACK,
  overflowX: "hidden",
  borderRadius: "7px",
  textTransform: "none",
  border: "none",
  padding: "0.3rem 0 0.3rem 0",
  margin: 0,
};

const ButtonsContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSelectedConversation",
})<{ isSelectedConversation: boolean }>(({ isSelectedConversation }) => ({
  display: "flex",
  flexFlow: "row",
  height: "2.625rem",
  alignItems: "center",
  padding: "0.3rem 0",
  paddingRight: "0.3rem",
  margin: "0.2rem 0",
  backgroundColor: isSelectedConversation ? Colors.ALMOST_WHITE : Colors.WHITE,
  borderRadius: "0.875rem",
  ":hover": {
    backgroundColor: Colors.ALMOST_WHITE,
  },
}));

const IconSx: SxProps = {
  borderRadius: "50%",
  transition: "all 0.5s ease",
  transform: "scale(1.2)",
  "&.MuiSvgIcon-root": {
    color: Colors.STONE_GRAY,
    fontSize: "large",
  },
  "&.MuiSvgIcon-root:hover": {
    backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
    color: Colors.LAZO_PURPLE_TEXT,
    cursor: "pointer",
  },
};

const StyledUserListButton = styled(Button)(({}) => ({
  display: "flex",
  minWidth: "fit-content",
  gap: "0.2rem",
  borderRadius: "0.2rem",
  padding: "0.5rem 0.4rem",
  border: `1px solid ${Colors.STONE_GRAY}`,
  backgroundColor: `${Colors.WHITE}`,
  height: "100%",
  alignItems: "center",
  transition: "all 0.25s ease",
  ":hover": {
    backgroundColor: `${Colors.BUTTON_SECONDARY_HOVER_PURPLE}`,
    cursor: "pointer",
  },
}));

const StyledOptionsContainer = styled(Box)(({}) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  height: "15px",
}));

function ConversationDrawerButtons({ conversation, optionalFunction }: ConversationDrawerButtonProps) {
  const [showUserListModal, setShowUserListModal] = useState<boolean>(false);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [showRenameModal, setShowRenameModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);
  const [showUndoDeleteModal, setShowUndoDeleteModal] = useState<boolean>(false);
  const [undoModalTimeout, setUndoModalTimeout] = useState<NodeJS.Timeout | null>(null);

  const [anchorElement, setAnchorElement] = React.useState<null | SVGSVGElement>(null);
  const openMenu = Boolean(anchorElement);

  const { selectedConversation, setSelectedConversation, resetSelectedConversation } = useSelectedConversation();
  //const { deleteConversation } = useConversations();

  function handleMoreClick(event: React.MouseEvent<SVGSVGElement>) {
    setAnchorElement(event.currentTarget);
  }

  const closeConversationOptions = () => {
    setAnchorElement(null);
  };

  const handleRenameTitle = (newTitle: string) => {
    if (!conversation.externalId) return;
    if (newTitle.length > 0 && newTitle !== conversation.title) {
      LazoChatConversationService.renameConversation(conversation.externalId, newTitle).then((response) => {
        response.onSuccess((updatedConversation) => {
          setSelectedConversation(updatedConversation);
          NotificationService.info("Conversation renamed succesfully");
        });
        response.onError((message, _) => {
          NotificationService.error(message);
        });
      });
    }
  };

  function handleSelectConversation() {
    setSelectedConversation(conversation);
  }

  const handleOnClickDelete = () => {
    setShowDeleteModal(true);
    closeConversationOptions();
  };

  const handleDelete = () => {
    if (!conversation.externalId) return;
    if (undoModalTimeout) {
      clearTimeout(undoModalTimeout);
      setUndoModalTimeout(null);
    }

    LazoChatConversationService.deleteConversation(conversation.externalId).then((response) => {
      response.onSuccess((deletedConversation) => {
        setShowDeleteModal(false);
        setShowUndoDeleteModal(true);
        setUndoModalTimeout(
          setTimeout(() => {
            handleCloseUndoDeleteConversationModal();
          }, 5000)
        );
      });

      response.onError((message, apiResponse) => {
        NotificationService.error(message);
      });
    });
  };

  function handleOnClickRename() {
    setShowRenameModal(true);
    closeConversationOptions();
  }

  const handleOnClickArchive = () => {
    if (!conversation.externalId) return;

    LazoChatConversationService.archiveConversation(conversation.externalId).then((response) => {
      response.onSuccess((archivedConversation) => {
        if (!LAZO_APPLICATIONS_OPTIONS_SERVICE.options.hideArchiveConversationModal) {
          setShowArchiveModal(true);
        } else {
          handleCloseArchiveConversationModal();
        }
      });

      response.onError((message, apiResponse) => {
        NotificationService.error(message);
      });
    });

    closeConversationOptions();
  };

  const handleUndoDelete = () => {
    if (!conversation.externalId) return;

    LazoChatConversationService.undoDeleteConversation(conversation.externalId).then((response) => {
      response.onSuccess((restoredConversation) => {
        NotificationService.info("Restoring conversation");
        setShowUndoDeleteModal(false);
      });

      response.onError((message, apiResponse) => {
        NotificationService.error(message);
      });
    });
  };

  const handleCloseArchiveConversationModal = () => {
    //deleteConversation(conversation);
    resetSelectedConversation();
    setShowArchiveModal(false);
    optionalFunction?.();
  };

  const handleDontShowAgainArchiveConversationModal = () => {
    //deleteConversation(conversation);
    resetSelectedConversation();
    optionalFunction?.();
    setShowArchiveModal(false);
    LAZO_APPLICATIONS_OPTIONS_SERVICE.options.hideArchiveConversationModal = true;
    LAZO_APPLICATIONS_OPTIONS_SERVICE.save();
  };

  const handleCloseUndoDeleteConversationModal = () => {
    //deleteConversation(conversation);
    resetSelectedConversation();
    setShowUndoDeleteModal(false);
    if (undoModalTimeout) {
      clearTimeout(undoModalTimeout);
    }
    setUndoModalTimeout(null);
    optionalFunction?.();
  };

  function handleUserListClick(): void {
    setShowUserListModal(true);
  }

  function handleUndoModalMouseEnter(): void {
    if (undoModalTimeout) {
      clearTimeout(undoModalTimeout);
    }
    setUndoModalTimeout(null);
  }

  function handleUndoModalMouseLeave(): void {
    if (undoModalTimeout) {
      clearTimeout(undoModalTimeout);
    }
    setUndoModalTimeout(
      setTimeout(() => {
        handleCloseUndoDeleteConversationModal();
      }, 5000)
    );
  }

  return (
    <>
      <ButtonsContainer
        isSelectedConversation={selectedConversation?.externalId === conversation.externalId}
        onMouseEnter={() => setShowOptions(true)}
        onMouseLeave={() => setShowOptions(false)}
      >
        <ToggleButton
          value={conversation}
          selected={selectedConversation?.externalId === conversation.externalId}
          onClick={() => {
            handleSelectConversation();
            //optionalFunction?.();
          }}
          sx={ConversationDrawerButtonSx}
        >
          <Box sx={{ paddingLeft: "1rem", textAlign: "start", width: "90%" }}>
            <Typography noWrap>{conversation.title}</Typography>
          </Box>
        </ToggleButton>
        <UnreadMessagesCountComponent unreadMessagesCount={conversation.unreadMessagesCount || 0} />
        <CommonStyledMenu open={openMenu} anchorEl={anchorElement} onClose={closeConversationOptions}>
          <StyledMenuItem onClick={handleOnClickRename}>
            <img src={EditChatIcon.src} alt="edit-conversation-icon" loading="lazy" fetchPriority="low" decoding="async" />
            <Typography>Rename conversation</Typography>
          </StyledMenuItem>
          <StyledMenuItem onClick={handleOnClickArchive}>
            {conversation.isArchived ? (
              <img src={UnarchiveIcon.src} alt="unarchive-conversation-icon" loading="lazy" fetchPriority="low" decoding="async" />
            ) : (
              <img src={ArchiveIcon.src} alt="archive-conversation-icon" loading="lazy" fetchPriority="low" decoding="async" />
            )}

            <Typography>{conversation.isArchived ? "Unarchive" : "Archive"} conversation</Typography>
          </StyledMenuItem>
          <StyledMenuItem className="red-item" onClick={handleOnClickDelete}>
            <img src={DeleteIcon.src} alt="delete-conversation-icon" loading="lazy" fetchPriority="low" decoding="async" />
            <Typography>Delete conversation</Typography>
          </StyledMenuItem>
        </CommonStyledMenu>
        <StyledOptionsContainer>
          {conversation.members && conversation.members.length > 1 && (
            <StyledUserListButton onClick={handleUserListClick}>
              <img src={UserSVG.src} loading="lazy" fetchPriority="low" decoding="async" />
              <Typography>{conversation.members.length}</Typography>
            </StyledUserListButton>
          )}
          {showOptions && <MoreHoriz sx={IconSx} onClick={(event) => handleMoreClick(event)} />}
        </StyledOptionsContainer>
        <RenameConversationModal
          conversationTitle={conversation.title || ""}
          showRenameModal={showRenameModal}
          handleOnClose={() => setShowRenameModal(false)}
          handleRenameTitle={handleRenameTitle}
        />

        <DeleteConversationModal
          showDeleteModal={showDeleteModal}
          conversationTitle={conversation.title || ""}
          handleOnClose={() => setShowDeleteModal(false)}
          handleDelete={handleDelete}
        />

        <ArchiveConversationModal
          dontShowAgainClick={handleDontShowAgainArchiveConversationModal}
          showArchiveModal={showArchiveModal}
          handleOnClose={handleCloseArchiveConversationModal}
        />

        <div onMouseEnter={handleUndoModalMouseEnter} onMouseLeave={handleUndoModalMouseLeave}>
          <UndoDeleteConversationModal
            conversationTitle={conversation.title || ""}
            showUndoDeleteModal={showUndoDeleteModal}
            handleOnClose={handleCloseUndoDeleteConversationModal}
            handleUndoDelete={handleUndoDelete}
          />
        </div>
      </ButtonsContainer>
      <UserListModal open={showUserListModal} handleClose={() => setShowUserListModal(false)} conversation={conversation} />
    </>
  );
}

export default ConversationDrawerButtons;
